import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Shield,
  Phone,
  Home,
  Star
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BudgetPage = () => {
  const features = [
    '10 Year Structural Warranty',
    'Family owned & operated',
    'Survey included in price',
    'Professional installation included',
    'VAT included',
    'Quality construction',
    'Range of colours available',
    'Suitable for all room types'
  ];

  const bestFor = [
    'First-time buyers',
    'Rental properties',
    'Budget-conscious homeowners',
    'Multi-room projects',
    'Investment properties',
    'New builds'
  ];

  const faqs = [
    {
      question: 'What makes Budget Shutters different from other ranges?',
      answer: 'Our Budget Shutters offer the same professional installation and quality service as our premium ranges, but at a more accessible price point. They are manufactured using cost-effective materials while maintaining excellent build quality and durability.'
    },
    {
      question: 'Are Budget Shutters suitable for bathrooms?',
      answer: 'While Budget Shutters can be used in many rooms, for high-moisture areas like bathrooms, we recommend our Polymer range which offers superior water resistance.'
    },
    {
      question: 'What warranty do Budget Shutters come with?',
      answer: 'Budget Shutters come with our comprehensive 10 Year Structural Warranty, giving you peace of mind about your investment.'
    },
    {
      question: 'Can I choose different colours?',
      answer: 'Yes! Our Budget range is available in a selection of popular colours to complement your interior design.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-50 to-green-100 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 mb-6">
                <span className="text-2xl">💰</span>
                <Badge className="bg-emerald-600 text-white">Best Value</Badge>
              </div>
              
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Budget Shutters
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Quality shutters at an affordable price point. Perfect for homeowners who want beautiful shutters 
                without breaking the bank. Every installation includes a professional survey, expert fitting, and full VAT.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Shield className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold text-gray-900">10 Year Warranty</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Star className="h-5 w-5 text-amber-500" />
                  <span className="font-semibold text-gray-900">Professional Installation</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 text-lg">
                    <Calculator className="mr-2 h-5 w-5" />
                    Use Our Online Estimator
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-4 text-lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Book Free Survey
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png"
                alt="Budget Shutters"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What's Included</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our Budget range delivers exceptional value with everything you need for a complete shutter installation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Best For Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Perfect For</h2>
              <p className="text-lg text-gray-600 mb-8">
                Budget Shutters are an excellent choice for a variety of situations where quality 
                and value go hand in hand.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {bestFor.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm">
                    <Home className="h-5 w-5 text-emerald-600" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png"
                alt="Budget Shutters Installation"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Common questions about our Budget Shutters range
            </p>
          </div>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 opacity-90">
            Get an instant estimate for Budget Shutters using our online calculator.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Use Our Online Estimator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/materials">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
              >
                Compare All Ranges
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BudgetPage;
